import { useCallback, useRef, useState } from 'react';
import type { PaginationInfo } from '../types';
import { getErrorMessage } from '../utils/errors';

/**
 * Offset-paginated list state with a "load more" (cumulative append) model.
 *
 * Encapsulates the state machine that was previously duplicated between the
 * app file browser and the admin uploads tab: the page/total counters, the
 * append-with-dedupe on Load More, and — the subtle part — a stale-response
 * guard so a slow page request can't leak its rows into a different scope
 * (folder) after the user has navigated away.
 *
 * The owning component fetches page 1 itself (typically alongside other data,
 * under a single loading flag) and seeds the hook with `setFirstPage(result)`;
 * the hook owns `loadMore()`. `scopeKey` identifies the current scope (e.g. the
 * folder id); a `loadMore` whose scope no longer matches on resolution is
 * discarded so its rows can't leak into a folder the user navigated to.
 */

export interface PaginatedFetchResult<T> {
  items: T[];
  pagination: PaginationInfo;
}

export interface UsePaginatedListOptions<T> {
  /** Identity of the current scope (e.g. folder id). A change invalidates in-flight requests. */
  scopeKey: string | null;
  /** Fetch a single 1-based page for the current scope. */
  fetchPage: (page: number) => Promise<PaginatedFetchResult<T>>;
}

export interface UsePaginatedList<T> {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  /** Seed page 1 with a result the caller already fetched (replaces items, resets paging). */
  setFirstPage: (result: PaginatedFetchResult<T>) => void;
  /** Fetch the next page and append it (deduped by id). */
  loadMore: () => void;
  /** Optimistically mutate the loaded items (e.g. rename in place, remove on delete). */
  setItems: (updater: (prev: T[]) => T[]) => void;
  /** Adjust the known total after an optimistic add/remove. */
  adjustTotal: (delta: number) => void;
}

export function usePaginatedList<T extends { id: string }>({
  scopeKey,
  fetchPage,
}: UsePaginatedListOptions<T>): UsePaginatedList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // Always reflects the scope currently in view, so a slow response can't leak
  // its rows into a scope the user has since navigated to.
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;

  const setFirstPage = useCallback((result: PaginatedFetchResult<T>) => {
    setItems(result.items);
    setPage(result.pagination.page);
    setTotal(result.pagination.total);
    setTotalPages(result.pagination.totalPages);
    setIsLoadingMore(false);
    setLoadMoreError(null);
  }, []);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    const requestScope = scopeKey;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await fetchPage(page + 1);
      if (scopeRef.current !== requestScope) return;
      setItems((prev) => {
        const seen = new Set(prev.map((it) => it.id));
        const additions = result.items.filter((it) => !seen.has(it.id));
        return additions.length === 0 ? prev : [...prev, ...additions];
      });
      setPage(result.pagination.page);
      setTotal(result.pagination.total);
      setTotalPages(result.pagination.totalPages);
    } catch (err) {
      if (scopeRef.current !== requestScope) return;
      setLoadMoreError(getErrorMessage(err));
    } finally {
      if (scopeRef.current === requestScope) setIsLoadingMore(false);
    }
  }, [scopeKey, fetchPage, page, isLoadingMore]);

  const adjustTotal = useCallback((delta: number) => {
    setTotal((prev) => Math.max(0, prev + delta));
  }, []);

  return {
    items,
    total,
    totalPages,
    page,
    hasMore: page < totalPages,
    isLoadingMore,
    loadMoreError,
    setFirstPage,
    loadMore,
    setItems,
    adjustTotal,
  };
}
